import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    TESSERACT_VERTICES,
    TESSERACT_EDGES,
    rotate3D,
    project4DTo3D,
    prefersReducedMotion,
} from '../utils/tesseractGeometry';

const NODE_LABELS = [
    'AI Strategy', 'ML Models', 'Analytics', 'Data Eng',
    'Deep Learning', 'MLOps', 'NLP', 'GenAI',
    'LLMs', 'Machine Vision', 'Cloud', 'Big Data',
    'Forecasting', 'Optimization', 'Neural Nets', 'AutoML',
];

const NODE_COLORS = [
    '#00b4d8', '#7c3aed', '#00b4d8', '#06d6a0',
    '#f72585', '#06d6a0', '#7c3aed', '#00b4d8',
    '#7c3aed', '#f72585', '#06d6a0', '#00b4d8',
    '#f72585', '#06d6a0', '#7c3aed', '#00b4d8',
];

const NODE_DETAILS = [
    {
        title: 'AI Strategy',
        subtitle: 'Strategic AI Roadmapping',
        description: 'We help organizations develop comprehensive AI strategies aligned with business objectives. From identifying high-impact use cases to building execution roadmaps, we ensure your AI investments deliver maximum ROI.',
        bullets: ['AI readiness assessments', 'Use case prioritization & ROI modeling', 'Technology stack selection', 'Organizational change management'],
    },
    {
        title: 'ML Models',
        subtitle: 'Custom Machine Learning Solutions',
        description: 'We design, build, and deploy custom machine learning models tailored to your specific business challenges — from classification and regression to recommendation systems and anomaly detection.',
        bullets: ['Supervised & unsupervised learning', 'Feature engineering & selection', 'Model validation & A/B testing', 'Production-grade model pipelines'],
    },
    {
        title: 'Analytics',
        subtitle: 'Advanced Data Analytics & BI',
        description: 'Transform raw data into actionable business intelligence. We build dashboards, reports, and analytical frameworks that give stakeholders real-time visibility into KPIs and trends.',
        bullets: ['Interactive dashboards & reporting', 'Statistical analysis & hypothesis testing', 'Customer segmentation & cohort analysis', 'Data storytelling & executive presentations'],
    },
    {
        title: 'Data Engineering',
        subtitle: 'Scalable Data Infrastructure',
        description: 'Build robust, scalable data pipelines and infrastructure that power your analytics and AI initiatives. We architect modern data platforms that handle petabyte-scale workloads.',
        bullets: ['ETL/ELT pipeline development', 'Data lake & warehouse architecture', 'Real-time streaming pipelines', 'Data quality & governance frameworks'],
    },
    {
        title: 'Deep Learning',
        subtitle: 'Neural Network Solutions',
        description: 'Leverage state-of-the-art deep learning architectures for complex problems like image recognition, speech processing, and sequential data analysis that traditional ML cannot solve.',
        bullets: ['CNNs, RNNs, Transformers', 'Transfer learning & fine-tuning', 'GPU-optimized training pipelines', 'Edge deployment & model compression'],
    },
    {
        title: 'MLOps',
        subtitle: 'ML Operations & Deployment',
        description: 'Bridge the gap between ML development and production with robust MLOps practices. We implement CI/CD for ML, model monitoring, and automated retraining pipelines.',
        bullets: ['CI/CD pipelines for ML models', 'Model versioning & experiment tracking', 'Automated monitoring & alerting', 'A/B testing & canary deployments'],
    },
    {
        title: 'NLP',
        subtitle: 'Natural Language Processing',
        description: 'Extract insights from text data at scale. From sentiment analysis and entity recognition to document summarization and chatbot development, we build NLP solutions that understand language.',
        bullets: ['Text classification & sentiment analysis', 'Named entity recognition (NER)', 'Document summarization & search', 'Conversational AI & chatbots'],
    },
    {
        title: 'GenAI',
        subtitle: 'Generative AI Solutions',
        description: 'Harness the power of generative AI to create content, automate workflows, and build intelligent assistants. We help you integrate GenAI responsibly into your products and operations.',
        bullets: ['Custom AI assistant development', 'Content generation pipelines', 'RAG (Retrieval-Augmented Generation)', 'Responsible AI governance & guardrails'],
    },
    {
        title: 'LLMs',
        subtitle: 'Large Language Model Integration',
        description: 'Fine-tune and deploy large language models for your specific domain. We optimize LLMs for accuracy, cost, and latency to deliver enterprise-grade language AI.',
        bullets: ['LLM fine-tuning & prompt engineering', 'Domain-specific model training', 'Cost optimization & model distillation', 'Enterprise API integration & security'],
    },
    {
        title: 'Machine Vision',
        subtitle: 'Computer Vision & Image AI',
        description: 'Build intelligent systems that see and understand visual data. From quality inspection to autonomous navigation, we develop computer vision solutions that drive automation.',
        bullets: ['Object detection & tracking', 'Image classification & segmentation', 'OCR & document processing', 'Video analytics & real-time processing'],
    },
    {
        title: 'Cloud',
        subtitle: 'Cloud AI & Infrastructure',
        description: 'Design and implement cloud-native AI architectures on AWS, Azure, or GCP. We optimize for performance, cost, and scalability while maintaining security best practices.',
        bullets: ['Cloud architecture design (AWS, Azure, GCP)', 'Serverless ML deployments', 'Cost optimization & auto-scaling', 'Security & compliance frameworks'],
    },
    {
        title: 'Big Data',
        subtitle: 'Big Data Processing & Analytics',
        description: 'Process and analyze massive datasets efficiently using distributed computing frameworks. We build systems that handle terabytes to petabytes of data with ease.',
        bullets: ['Spark, Hadoop & distributed computing', 'Real-time stream processing (Kafka, Flink)', 'Data lakehouse architecture', 'Large-scale batch processing pipelines'],
    },
    {
        title: 'Forecasting',
        subtitle: 'Predictive Forecasting & Planning',
        description: 'Build accurate forecasting models for demand planning, financial projections, and resource optimization. We combine statistical methods with ML for superior predictions.',
        bullets: ['Time series analysis & forecasting', 'Demand planning & inventory optimization', 'Financial modeling & risk assessment', 'Scenario analysis & simulation'],
    },
    {
        title: 'Optimization',
        subtitle: 'Mathematical Optimization',
        description: 'Solve complex business optimization problems — from supply chain routing to resource allocation. We apply operations research and AI to find optimal solutions.',
        bullets: ['Supply chain & logistics optimization', 'Pricing & revenue optimization', 'Resource allocation & scheduling', 'Constraint programming & simulation'],
    },
    {
        title: 'Neural Nets',
        subtitle: 'Neural Network Architecture Design',
        description: 'Design custom neural network architectures optimized for your specific data and performance requirements. We push the boundaries of what\'s possible with cutting-edge research.',
        bullets: ['Custom architecture design', 'Attention mechanisms & transformers', 'Graph neural networks', 'Neural architecture search (NAS)'],
    },
    {
        title: 'AutoML',
        subtitle: 'Automated Machine Learning',
        description: 'Accelerate your ML development with automated pipelines that handle feature engineering, model selection, and hyperparameter tuning — delivering production models faster.',
        bullets: ['Automated feature engineering', 'Model selection & hyperparameter tuning', 'AutoML pipeline development', 'Self-improving model systems'],
    },
];

// Project 4D -> 3D (static perspective), then rotate in 3D, then project to 2D
function projectAndRotate(point4d, rotX, rotY, rotZ) {
    // Step 1: Fixed 4D -> 3D perspective projection (creates the tesseract shape)
    const p3 = project4DTo3D(point4d);

    // Step 2: Rotate rigidly in 3D space
    const { x: x3, y: y3, z: z3 } = rotate3D(p3.x, p3.y, p3.z, rotX, rotY, rotZ);

    // Step 3: 3D -> 2D perspective projection
    const distance3d = 3.5;
    const p = 1 / (distance3d - z3);

    return { x: x3 * p, y: y3 * p, depth: z3 };
}

const InteractiveNetwork = () => {
    const [angles, setAngles] = useState({ rotX: 0, rotY: 0, rotZ: 0 });
    const [hovered, setHovered] = useState(null);
    const [clicked, setClicked] = useState(null);
    const [modalNode, setModalNode] = useState(null);
    const animRef = useRef(null);
    const lastTime = useRef(Date.now());
    const targetAngles = useRef(null);
    const focusTimer = useRef(null);
    const isDragging = useRef(false);
    const lastMouse = useRef({ x: 0, y: 0 });
    const containerRef = useRef(null);

    // Drag handlers for rotating the cube
    const handlePointerDown = useCallback((e) => {
        // Don't start drag on node clicks
        if (e.target.closest && e.target.closest('[data-network-node]')) return;
        isDragging.current = true;
        lastMouse.current = { x: e.clientX, y: e.clientY };
        targetAngles.current = null; // Cancel any target animation
        e.preventDefault();
    }, []);

    const handlePointerMove = useCallback((e) => {
        if (!isDragging.current) return;
        const dx = e.clientX - lastMouse.current.x;
        const dy = e.clientY - lastMouse.current.y;
        lastMouse.current = { x: e.clientX, y: e.clientY };

        // Convert pixel movement to rotation (sensitivity factor)
        const sensitivity = 0.007;
        setAngles(prev => ({
            rotX: prev.rotX - dy * sensitivity,
            rotY: prev.rotY + dx * sensitivity,
            rotZ: prev.rotZ,
        }));
    }, []);

    const handlePointerUp = useCallback(() => {
        if (!isDragging.current) return;
        isDragging.current = false;
        // Auto-rotation resumes naturally once dragging stops
    }, []);

    // Attach global pointer events for drag (so dragging works even outside the SVG)
    useEffect(() => {
        const onMove = (e) => handlePointerMove(e);
        const onUp = () => handlePointerUp();
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [handlePointerMove, handlePointerUp]);

    // Animation loop (respects prefers-reduced-motion)
    useEffect(() => {
        const reducedMotion = prefersReducedMotion();
        const animate = () => {
            const now = Date.now();
            const dt = (now - lastTime.current) / 1000;
            lastTime.current = now;

            setAngles(prev => {
                if (targetAngles.current) {
                    // Smoothly interpolate toward target
                    const lerp = (a, b, t) => a + (b - a) * t;
                    const speed = 3.5 * dt;
                    const next = {
                        rotX: lerp(prev.rotX, targetAngles.current.rotX, speed),
                        rotY: lerp(prev.rotY, targetAngles.current.rotY, speed),
                        rotZ: lerp(prev.rotZ, targetAngles.current.rotZ, speed),
                    };
                    const dist = Math.abs(next.rotX - targetAngles.current.rotX) +
                        Math.abs(next.rotY - targetAngles.current.rotY) +
                        Math.abs(next.rotZ - targetAngles.current.rotZ);
                    if (dist < 0.01) {
                        targetAngles.current = null;
                    }
                    return next;
                }
                // Normal auto-rotation (smooth 3D tumble) — pause while dragging
                // or when the user prefers reduced motion
                if (isDragging.current || reducedMotion) return prev;
                return {
                    rotX: prev.rotX + dt * 0.25,
                    rotY: prev.rotY + dt * 0.35,
                    rotZ: prev.rotZ + dt * 0.1,
                };
            });

            animRef.current = requestAnimationFrame(animate);
        };
        animRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animRef.current);
    }, []);

    // Compute projected vertices using fixed 4D→3D then 3D rotation
    const projectedVertices = TESSERACT_VERTICES.map(v => {
        const proj = projectAndRotate(v, angles.rotX, angles.rotY, angles.rotZ);
        return {
            x: 300 + proj.x * 650,
            y: 300 + proj.y * 650,
            depth: proj.depth,
        };
    });

    const handleNodeClick = useCallback((idx) => {
        setModalNode(idx);
        setClicked(prev => prev === idx ? null : idx);

        // Get the fixed 3D position of this vertex (after 4D→3D projection, before 3D rotation)
        const p3 = project4DTo3D(TESSERACT_VERTICES[idx]);

        // Apply current 3D rotation to find its current position
        const { x: cx, y: cy, z: cz } = rotate3D(p3.x, p3.y, p3.z, angles.rotX, angles.rotY, angles.rotZ);

        // Calculate additional rotations to bring this vertex to the front (max z)
        // First rotate around Y to zero out x component
        const deltaY = -Math.atan2(cx, cz);
        // After Y rotation, recalculate position
        const cos = Math.cos(deltaY), sin = Math.sin(deltaY);
        const rz = cx * sin + cz * cos;
        // Then rotate around X to zero out y component
        const deltaX = Math.atan2(cy, rz);

        targetAngles.current = {
            rotX: angles.rotX + deltaX,
            rotY: angles.rotY + deltaY,
            rotZ: angles.rotZ,
        };

        // Resume auto-rotation after 5 seconds
        clearTimeout(focusTimer.current);
        focusTimer.current = setTimeout(() => {
            targetAngles.current = null;
        }, 5000);
    }, [angles]);

    const closeModal = useCallback(() => {
        setModalNode(null);
        setClicked(null);
    }, []);

    // Sort edges by average depth for painter's algorithm
    const sortedEdges = [...TESSERACT_EDGES].sort((a, b) => {
        const depthA = (projectedVertices[a[0]].depth + projectedVertices[a[1]].depth) / 2;
        const depthB = (projectedVertices[b[0]].depth + projectedVertices[b[1]].depth) / 2;
        return depthA - depthB;
    });

    // Sort nodes by depth
    const sortedNodes = projectedVertices.map((v, i) => ({ ...v, idx: i }))
        .sort((a, b) => a.depth - b.depth);

    const activeDetail = modalNode !== null ? NODE_DETAILS[modalNode] : null;
    const activeColor = modalNode !== null ? NODE_COLORS[modalNode] : '#00b4d8';

    return (
        <div
            ref={containerRef}
            onPointerDown={handlePointerDown}
            style={{ width: '100%', maxWidth: 650, aspectRatio: '1', position: 'relative', touchAction: 'none', cursor: isDragging.current ? 'grabbing' : 'grab' }}
        >
            <svg viewBox="0 0 600 600" width="100%" height="100%" style={{ overflow: 'visible' }}>
                <defs>
                    {NODE_COLORS.map((c, i) => (
                        <radialGradient key={`g${i}`} id={`tg-${i}`}>
                            <stop offset="0%" stopColor={c} stopOpacity="0.5" />
                            <stop offset="100%" stopColor={c} stopOpacity="0" />
                        </radialGradient>
                    ))}
                </defs>

                {/* Edges */}
                {sortedEdges.map(([a, b], i) => {
                    const va = projectedVertices[a];
                    const vb = projectedVertices[b];
                    const avgDepth = (va.depth + vb.depth) / 2;
                    const opacity = 0.12 + Math.max(0, (avgDepth + 2) / 4) * 0.35;
                    const isActive = hovered === a || hovered === b || clicked === a || clicked === b;
                    return (
                        <line
                            key={`e${i}`}
                            x1={va.x} y1={va.y}
                            x2={vb.x} y2={vb.y}
                            stroke={isActive ? '#00b4d8' : `rgba(0,180,216,${opacity.toFixed(2)})`}
                            strokeWidth={isActive ? 2 : 1}
                        />
                    );
                })}

                {/* Nodes sorted by depth (back to front) */}
                {sortedNodes.map(({ x, y, depth, idx }) => {
                    const color = NODE_COLORS[idx];
                    const depthScale = 0.6 + Math.max(0, (depth + 2) / 4) * 0.6;
                    const radius = 14 * depthScale;
                    const isHov = hovered === idx;
                    const isClicked = clicked === idx;
                    const isActive = isHov || isClicked;
                    const nodeOpacity = 0.4 + Math.max(0, (depth + 2) / 4) * 0.6;

                    return (
                        <g
                            key={NODE_LABELS[idx]}
                            data-network-node
                            role="button"
                            tabIndex={0}
                            aria-label={`Learn more about ${NODE_LABELS[idx]}`}
                            onClick={() => handleNodeClick(idx)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleNodeClick(idx);
                                }
                            }}
                            onMouseEnter={() => setHovered(idx)}
                            onMouseLeave={() => setHovered(null)}
                            style={{ cursor: 'pointer', outline: 'none' }}
                        >
                            {/* Glow */}
                            <circle
                                cx={x} cy={y}
                                r={radius * 3}
                                fill={`url(#tg-${idx})`}
                                opacity={isActive ? 0.9 : 0.3}
                            />
                            {/* Outer ring */}
                            <circle
                                cx={x} cy={y}
                                r={radius * (isActive ? 1.2 : 1)}
                                fill="rgba(10,25,41,0.85)"
                                stroke={color}
                                strokeWidth={isActive ? 2.5 : 1.5}
                                opacity={nodeOpacity}
                            />
                            {/* Inner dot */}
                            <circle
                                cx={x} cy={y}
                                r={radius * 0.4 * (isActive ? 1.3 : 1)}
                                fill={color}
                                opacity={isActive ? 1 : nodeOpacity * 0.8}
                            />
                            {/* Label */}
                            <text
                                x={x}
                                y={y + radius + 13}
                                textAnchor="middle"
                                fill={isActive ? '#ffffff' : `rgba(255,255,255,${(nodeOpacity * 0.7).toFixed(2)})`}
                                fontSize={isActive ? '13' : `${11 * depthScale}`}
                                fontFamily="Inter, sans-serif"
                                fontWeight={isActive ? '700' : '400'}
                                style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                                {NODE_LABELS[idx]}
                            </text>
                            {/* Click tooltip */}
                            {isClicked && (
                                <g>
                                    <rect
                                        x={x - 55} y={y - radius - 32}
                                        width={110} height={24}
                                        rx={6}
                                        fill="rgba(0,0,0,0.8)"
                                        stroke={color}
                                        strokeWidth={1}
                                    />
                                    <text
                                        x={x} y={y - radius - 16}
                                        textAnchor="middle"
                                        fill="#ffffff"
                                        fontSize="10"
                                        fontFamily="Inter, sans-serif"
                                        fontWeight="600"
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        {NODE_LABELS[idx]}
                                    </text>
                                </g>
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Service Detail Modal */}
            {activeDetail && (
                <div className="tesseract-modal-overlay" onClick={closeModal}>
                    <div
                        className="tesseract-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ '--modal-accent': activeColor }}
                    >
                        <button className="tesseract-modal-close" onClick={closeModal} aria-label="Close dialog">✕</button>
                        <div className="tesseract-modal-header">
                            <div className="tesseract-modal-icon" style={{ background: activeColor }}>
                                <span>{activeDetail.title.charAt(0)}</span>
                            </div>
                            <div>
                                <h3 className="tesseract-modal-title">{activeDetail.title}</h3>
                                <p className="tesseract-modal-subtitle">{activeDetail.subtitle}</p>
                            </div>
                        </div>
                        <p className="tesseract-modal-desc">{activeDetail.description}</p>
                        <div className="tesseract-modal-bullets">
                            <h4>What We Deliver</h4>
                            <ul>
                                {activeDetail.bullets.map((b) => (
                                    <li key={b}>
                                        <span className="bullet-dot" style={{ background: activeColor }}></span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <Link to="/contact" className="tesseract-modal-cta" style={{ background: activeColor }}>
                            Discuss This Service →
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InteractiveNetwork;
